import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Send, Bug, MessageSquare, Newspaper, HelpCircle } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = ["Bug Report", "Feedback", "Media / Press", "Other"];

const FAQS = [
  {
    q: "When is Flappy Goal releasing?",
    a: "We're targeting launch later this year on iOS and Android. The download page will light up the moment we're live.",
  },
  {
    q: "Is Flappy Goal really free?",
    a: "Yes. The full game is free to play. Optional cosmetics like ball skins can be earned just by playing — no paywall on the pitch.",
  },
  {
    q: "Does it work offline?",
    a: "Absolutely. You only need a connection for leaderboards and daily events. Everything else runs fully offline.",
  },
  {
    q: "I found a bug in the demo. Where do I report it?",
    a: "Right here! Pick 'Bug Report' in the form and tell us what happened — screenshots and your phone model help a ton.",
  },
  {
    q: "Can I stream or make videos of the game?",
    a: "Please do. You have full permission to stream, record and monetise Flappy Goal content. Tag us so we can share it.",
  },
];

const TOPIC_ICONS = { "Bug Report": Bug, Feedback: MessageSquare, "Media / Press": Newspaper, Other: HelpCircle };

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "Feedback", message: "" });
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", topic: "Feedback", message: "" });
    } catch (err) {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-pitch/70 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-volt/70";

  return (
    <div data-testid="contact-page" className="pt-36">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-4">
            Contact Us
          </p>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl tracking-tight leading-none">
            TALK TO <span className="text-volt">THE DUGOUT</span>
          </h1>
          <p className="mt-6 max-w-xl text-slate-300 leading-relaxed">
            Bugs, feedback, press kits or just your highest score — we read
            everything. Average reply time: faster than a counter-attack.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-line bg-turf/70 p-8 sm:p-10 space-y-5"
          >
            <div>
              <label htmlFor="contact-name" className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
                Your name
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Alex Ferguson"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
                Email
              </label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-topic" className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
                Topic
              </label>
              <select
                id="contact-topic"
                data-testid="contact-topic-select"
                value={form.topic}
                onChange={set("topic")}
                className={`${inputCls} appearance-none`}
              >
                {TOPICS.map((t) => (
                  <option key={t} value={t} className="bg-pitch">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
                Message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us everything…"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="inline-flex items-center gap-2.5 rounded-full bg-volt px-7 py-3.5 font-bold text-pitch transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-60 disabled:hover:scale-100"
            >
              <Send size={17} strokeWidth={2.5} />
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <div>
            <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">
              Quick answers
            </h2>
            <div className="mt-7 space-y-3" data-testid="faq-list">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div
                    key={i}
                    data-testid={`faq-item-${i}`}
                    className="rounded-2xl border border-line bg-turf/60 overflow-hidden"
                  >
                    <button
                      type="button"
                      data-testid={`faq-trigger-${i}`}
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left"
                    >
                      <span className="font-semibold text-sm sm:text-base">{f.q}</span>
                      <ChevronDown
                        size={18}
                        className={`shrink-0 text-volt transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <p
                            className="px-6 pb-5 text-sm text-slate-400 leading-relaxed"
                            data-testid={`faq-answer-${i}`}
                          >
                            {f.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 rounded-2xl border border-line bg-turf/40 p-6 flex items-center gap-4">
              {(() => {
                const Icon = TOPIC_ICONS[form.topic] || HelpCircle;
                return <Icon size={22} className="text-gold shrink-0" />;
              })()}
              <p className="text-sm text-slate-400">
                Prefer email? Write to{" "}
                <span className="text-volt font-semibold">hello@flappygoal.game</span>{" "}
                and we'll pick it up between matches.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
